import { useAccountStore } from '@/stores/useAccountStore'
import { useCurrentReadsStore } from '@/stores/useCurrentReadsStore'
import { useFormsStore } from '@/stores/useFormsStore'
import { defineStore } from 'pinia'
import { io } from 'socket.io-client'
import { computed, reactive, ref, type Ref } from 'vue'
// useMapStore and useSettingsStore are imported but only instantiated inside actions to avoid circular deps
import { useHttpClient } from '@/composables/useHttpClient'
import { useLogError } from '@/composables/useLogError'
import { usePreprocessCube } from '@/composables/usePreprocessCube.ts'
import { Constants, NOTIFICATION_TYPES_MAP } from '@/constants'
import { useMapStore } from '@/stores/useMapStore'
import { useSettingsStore } from '@/stores/useSettingsStore'
import type { CubeDetails, InfoWindowImageDetails } from '@/types/CubeDto'
import { BooleanUtil } from '@/util/BooleanUtil'
import { useNotificationsStore } from './useNotificationsStore'

export const useCubeStore = defineStore('cube', () => {
  const { logError } = useLogError()
  const { backendApi, reemApi, proxyApi, balenaApi } = useHttpClient()
  const mapStore = useMapStore()
  const notificationStore = useNotificationsStore()

  // --- State ---

  // Core identity
  const cube: Ref<CubeDetails | null> = ref(null)
  const id = ref(false)
  const name = ref<string | null>(null)
  const nickName = ref(null)
  const hasProduction = ref(false)
  const hasConsumption = ref(false)
  const productionType = ref('')
  const latitude = ref<number | false>(false)
  const longitude = ref<number | false>(false)
  const unusedEnergyLines = ref(0)
  const openConnectionsAvailable = ref(0)
  const totalEnergyLinesAsConsumer = ref(0)
  const isGrantedGridFeeDiscount = ref(false)

  // Plan
  const planName = ref<string | null>(null)
  const planFrom = ref<Date | null>(null)
  const planTo = ref<Date | null>(null)

  // Hardware
  const iotDeviceId = ref(null)
  const meterType = ref('')
  const useThirdPartyMeter = ref(false)
  const showInverterData = ref(false)
  const showBatteryData = ref(false)
  const hasSeparateMeterHardware = ref(false)

  // Connections (populated via setCubeConnections)
  const producers: Ref<any[]> = ref([])
  const consumers: Ref<any[]> = ref([])
  const connectionsReceived = ref(false)
  // Notifications
  const notifications = ref([])
  const notificationsValue = ref(0)
  const webPushValue = ref(0)
  const emailSubscription = ref(false)

  // Votes / likes
  const premiumFeatures = ref([])
  const likeableCubes = ref([])
  const likedCubes = ref([])

  // Supplier
  const supplier = reactive<Record<string, any>>({
    name: false,
    shortName: false,
    logoUrl: false,
    iconUrl: false,
    phone: "",
    email: ""
  })

  // Network
  const localHostName = ref<string | false>(false)

  // Socket (static config, set at init time)
  const meterSocketHost = ref<string | null>(null)
  const meterSocketPort = ref<number | null>(null)
  // const meterSocketPath = ref<string | null>(null)
  const deviceManagerSocketHost = ref<string | null>(null)
  const deviceManagerSocketPort = ref<number | null>(null)
  const deviceManagerSocketPath = ref<string | null>(null)
  const socketConfig = reactive({
    reconnectionDelay: 10000,
    randomizationFactor: 0,
    transport: ['websocket'],
  })

  const meterSocket = ref<ReturnType<typeof io> | null>(null)
  const deviceManagerSocket = ref<ReturnType<typeof io> | null>(null)

  // Watch face
  const watchFace = ref('Classic')
  const watchFaceBrightness = ref('5')
  const watchFaceAuto = ref('auto')
  const watchFaceTimerActive = ref(false)
  const watchFaceTimerBrightness = ref('1')
  const watchFaceTimerSlot = reactive({ start: 7, end: 22 })

  // Description / images / CLP
  const descriptionText = ref('')
  const publicContactPhoneNumber = ref('')
  const images: Ref<InfoWindowImageDetails | null> = ref(null)
  const cubeCLP = reactive({
    consumptionCLP: 'H0',
    productionCLP: '',
    tradingPrognosisScenario: 'eF Prognosis + CLP',
    energyUsageConsumptionMWh: 2,
    energyUsageProductionMWh: 0,
  })

  // --- Computed ---

  const isCubeSelected = computed(() => !!name.value)

  const meterSocketUrl = computed(
    () => `https://${meterSocketHost.value}:${meterSocketPort.value}/v3/app`
  )

  const deviceManagerSocketUrl = computed(
    () => `https://${deviceManagerSocketHost.value}:${deviceManagerSocketPort.value}${(deviceManagerSocketPath.value || '').replace(/\/$/, '')}`
  )

  const homeCoordinates = computed(() => ({
    lat: latitude.value,
    lng: longitude.value,
  }))

  const unreadNotifications = computed(() =>
    notifications.value.filter(n => n.unread === 1)
  )

  const pushNotificationsEnabled = computed(() =>
    webPushValue.value > 0 && notificationsValue.value > 0
  )

  // TODO is in accountStore.properties
  const isCommunityless = computed(() => {
    return cube.value?.appConfig?.options?.isCommunityless ?? false
  });

  function numNotificationsForTypeGroup(group: string, onlyUnread = true): number {
    let notifs = notifications.value
    if (onlyUnread) {
      notifs = notifs.filter((n) => n.unread === 1)
    }
    if (group === 'any') {
      return notifs.length
    }
    if (!Object.keys(NOTIFICATION_TYPES_MAP).includes(group)) {
      return 0
    }
    const types = NOTIFICATION_TYPES_MAP[group]
    return notifs.filter((n) => types.indexOf(n.type_name) > -1).length
  }

  /**
   * Populate cube state from the getAppConfig API response.
   * Called by useAppStore.init() after the getAppConfig request resolves.
   */
  function setFromAppConfig(data) {
    cube.value = data.data

    const appConfig = data.data.appConfig
    const cubeSocketConnection = data.data.cubeDetails
    const customer = data.data.customerInfo
    const cubeAuthData = data.data.cubeAuthData
    const sessionSettings = data.data.sessionSettings

    id.value = data.data.cubeId
    name.value = sessionSettings.selectedCubeName
    nickName.value = customer.nickName ?? cubeAuthData.accessObjects[sessionSettings.selectedCubeName]
    isGrantedGridFeeDiscount.value = data.data.netzkostenersparnis_applies

    supplier.logoUrl = appConfig.supplierLogo
    supplier.iconUrl = appConfig.supplierIcon

    let cubeUrl: string | null = null;

    if (customer.isCubelessRemoteCube == "1" || customer.useRemoteCube == "1") {
      cubeUrl = `${data.data.cubeName}-api-remotecube.remote-cubes.${Constants.HOSTNAME}`
    } else {
      cubeUrl = `${customer.iotDeviceId}.balena-devices.com`
    }

    hasSeparateMeterHardware.value = customer.isCubelessRemoteCube == "0"
    localHostName.value = cubeSocketConnection.localIp || false

    // Socket config — set once at init, does not change during session
    meterSocketHost.value = cubeUrl
    meterSocketPort.value = 443

    deviceManagerSocketHost.value = cubeUrl
    deviceManagerSocketPort.value = 443
    deviceManagerSocketPath.value = cubeSocketConnection.remoteWebSocketPath || '/app'

    notifications.value = data.data.subscription.inbox
    notificationsValue.value = parseInt(data.data.subscription.notificationEnabled)
    webPushValue.value = parseInt(data.data.subscription.subscriptionEnabled)
    emailSubscription.value = BooleanUtil.isTrue(data.data.subscription.emailSubscriptionActive)

    // Customer properties
    hasConsumption.value = customer.hasConsumption === true || customer.hasConsumption === '1'
    hasProduction.value = customer.hasProduction === true || customer.hasProduction === '1'
    productionType.value = customer.productionType.toLowerCase()
    latitude.value = parseFloat(customer.latitude)
    longitude.value = parseFloat(customer.longitude)
    unusedEnergyLines.value = parseInt(customer.energyLinesAvailable)
    openConnectionsAvailable.value = parseInt(customer.openConnectionsAvailable)
    showInverterData.value = customer.useInverterData === '1'
    showBatteryData.value = customer.useBatteryData === '1'
    planName.value = customer.tariff
    planFrom.value = customer.displayFlexDateFrom ? new Date(customer.displayFlexDateFrom) : null
    planTo.value = customer.displayFlexDateTo ? new Date(customer.displayFlexDateTo) : null
    iotDeviceId.value = customer.iotDeviceId
    useThirdPartyMeter.value = customer.useThirdPartyMeter
    meterType.value = customer.meterType
    descriptionText.value = customer.infoWindowText
    publicContactPhoneNumber.value = customer.publicContactPhoneNumber ?? ''
    totalEnergyLinesAsConsumer.value = customer.totalEnergyLinesAsConsumer
    images.value = customer.infoWindowImageDetails
    premiumFeatures.value = customer.premiumFeatures

    Object.assign(cubeCLP, {
      consumptionCLP: customer.consumptionCLP,
      productionCLP: customer.productionCLP,
      tradingPrognosisScenario: customer.tradingPrognosisScenario,
      energyUsageConsumptionMWh: customer.energyUsageConsumptionMWh,
      energyUsageProductionMWh: customer.energyUsageProductionMWh,
    })

    watchFace.value = customer.selectedWatchFace
    const watchSettings = customer.watchSettings
    if (watchSettings) {
      watchFaceBrightness.value = watchSettings.brightnessLevel?.toString()
      watchFaceAuto.value = watchSettings.brightnessManagement
      if (watchSettings.schedule !== undefined) {
        watchFaceTimerActive.value =
          watchSettings.schedule?.startHour !== undefined &&
          watchSettings.schedule?.endHour !== undefined &&
          (watchSettings.schedule.startHour !== 0 || watchSettings.schedule.endHour !== 24)
        watchFaceTimerBrightness.value =
          watchSettings.schedule?.outOfRangeBrightnessLevel?.toString()
        watchFaceTimerSlot.start = watchSettings.schedule.startHour
        watchFaceTimerSlot.end = watchSettings.schedule.endHour
      }
    }

    likeableCubes.value = data.data.votingInformation?.votableCubes || []
    likedCubes.value = data.data.votingInformation?.votedCubes || []
  }

  function setSupplierData(data) {
    Object.assign(supplier, data)
  }

  /** Set producers and consumers after a connections update. */
  function setCubeConnections(p, c) {
    producers.value = p
    consumers.value = c
    connectionsReceived.value = true
  }

  function addNotification(notification) {
    notifications.value.unshift(notification)
  }

  // --- Private helpers ---

  const { preprocessCube } = usePreprocessCube()

  function applyConnectionsFromResponse(data) {
    const homeCoords = { lat: latitude.value, lng: longitude.value }
    producers.value = Object.keys(data.data.producers).map((cubeName) =>
      preprocessCube({ cubeName, ...data.data.producers[cubeName] }, homeCoords)
    )
    consumers.value = Object.keys(data.data.consumers).map((cubeName) =>
      preprocessCube({ cubeName, ...data.data.consumers[cubeName] }, homeCoords)
    )
    connectionsReceived.value = true
  }

  // --- Actions ---

  async function updateCubeConnections() {
    try {
      const result = await backendApi.get('/app/getCubeConnections', {
        params: { cubeName: name.value },
      })

      if (!result?.data?.bSuccess || result.data.error) {
        throw new Error(`GetCubeConnections request returned with error: ${result.data.error?.Message}`)
      }

      applyConnectionsFromResponse(result.data)
    } catch (err) {
      logError('Requesting cube connections failed: ' + err)
    }
  }

  function setupMeterSocket() {
    const currentReads = useCurrentReadsStore()
    try {
      const socket = io(meterSocketUrl.value, socketConfig)
      meterSocket.value = socket

      socket.on('connect', () => {
        currentReads.onConnectionEstablished()
      })
      socket.on('connect_error', () => {
        currentReads.onConnectionLost()
      })

      socket.on('TRADING_METERING_SUMMARY', (rawData) => {
        // Currently the meter data returned by cubes is in an old format. We need
        // to wrap/convert it to the new format.
        currentReads.meterDataReceived({ lastMinute: { power: rawData.aggregatedData } })
      })
    } catch (err) {
      logError(`Error setting up socket connection: ${err}`)
    }
  }

  async function connectToCube(producer) {
    const formsStore = useFormsStore()
    const mapStore = useMapStore()

    formsStore.setFormState(`connectToCube_${producer.cubeName}`, 'submitting')

    try {
      const result = await backendApi.get('/app/connectToProducer', {
        params: { ownCubeName: name.value, producerCubeName: producer.cubeName },
      })

      if (!result?.data?.bSuccess || result.data.error) {
        if (result.data.message?.toLowerCase() === 'connection already present') {
          throw new Error('Connection already present.')
        }
        throw new Error(`Request failed: ${result.data.error} ${result.data.message}.`)
      }

      const updatedProducer = {
        ...producer,
        params: result.data.data.producers[producer.cubeName].params,
        tradingDetails: result.data.data.producers[producer.cubeName].tradingDetails,
      }

      producers.value = [...producers.value, updatedProducer]
      unusedEnergyLines.value = parseInt(result.data.updatedCube.params.energyLinesAvailable)

      mapStore.patchGlobalCube(producer.cubeName, { isProducer: true })

      formsStore.setFormState(`connectToCube_${producer.cubeName}`, 'finished')
    } catch (err) {
      logError(`Error connecting to producer ${producer.cubeName}: ${err}.`)

      formsStore.setFormState(`connectToCube_${producer.cubeName}`, false)
    }
  }

  async function disconnectFromCube(producer) {
    const mapStore = useMapStore()
    try {
      const response = await backendApi.get('/app/disconnectFromProducer', {
        params: { ownCubeName: name.value, producerCubeName: producer.cubeName },
      })
      producers.value = producers.value.filter((p) => p.cubeName !== producer.cubeName)
      unusedEnergyLines.value = parseInt(response.data.updatedCube.params.energyLinesAvailable)
      const existing = mapStore.getCubeByName(producer.cubeName)
      mapStore.patchGlobalCube(producer.cubeName, {
        isProducer: false,
        openConnectionsAvailable: (existing?.openConnectionsAvailable ?? 0) + 1,
      })
    } catch (err) {
      logError(`Error disconnecting from producer: ${err}.`)
    }
  }

  async function donateToCube(consumer) {
    const formsStore = useFormsStore()
    const mapStore = useMapStore()
    const accountStore = useAccountStore()

    if (accountStore.isDemoUser) {
      return
    }

    formsStore.setFormState(`donateToCube_${consumer.cubeName}`, 'submitting')
    try {
      const settingsStore = useSettingsStore()
      const resDonate = backendApi.get('/app/donateToConsumer', {
        params: { producerCubeName: name.value, donationRecipient: consumer.cubeName },
      })
      const resPrice = settingsStore.saveSpecialSellingPriceForConsumer({ price: 0, consumer: consumer.cubeName })
      const results = await Promise.all([resDonate, resPrice])
      if (!results[0]?.data?.bSuccess || results[0].data.error) {
        throw new Error(`Request failed: ${results[0].data.error} ${results[0].data.message}.`)
      }
      applyConnectionsFromResponse(results[0].data)
      const existing = mapStore.getCubeByName(consumer.cubeName)
      mapStore.patchGlobalCube(consumer.cubeName, {
        isDonatee: true,
        openConnectionsAvailable: (existing?.openConnectionsAvailable ?? 1) - 1,
      })
      formsStore.setFormState(`donateToCube_${consumer.cubeName}`, 'finished')
    } catch (err) {
      logError(`Error connecting to consumer ${consumer.cubeName}: ${err}.`)
      formsStore.setFormState(`donateToCube_${consumer.cubeName}`, false)
    }
  }

  async function stopDonatingToCube(consumer) {
    const formsStore = useFormsStore()
    const mapStore = useMapStore()
    formsStore.setFormState(`stopDonatingToCube_${consumer.cubeName}`, 'submitting')
    try {
      const settingsStore = useSettingsStore()
      const resDonate = backendApi.get('/app/disconnectDonationRecipient', {
        params: { producerCubeName: name.value, donationRecipient: consumer.cubeName },
      })
      const resPrice = settingsStore.removeSpecialPriceForConsumer(consumer.cubeName)
      const results = await Promise.all([resDonate, resPrice])
      if (!results[0]?.data?.bSuccess || results[0].data.error) {
        throw new Error(`Disconnect donate request failed: ${results[0].data.error} ${results[0].data.message}.`)
      }
      applyConnectionsFromResponse(results[0].data)
      const existing = mapStore.getCubeByName(consumer.cubeName)
      mapStore.patchGlobalCube(consumer.cubeName, {
        isDonatee: false,
        openConnectionsAvailable: (existing?. openConnectionsAvailable ?? 0) + 1,
      })
      formsStore.setFormState(`stopDonatingToCube_${consumer.cubeName}`, 'finished')
    } catch (err) {
      logError(`Error disconnecting from consumer ${consumer.cubeName}: ${err}.`)
      formsStore.setFormState(`stopDonatingToCube_${consumer.cubeName}`, false)
    }
  }

  async function likeCube(producerName) {
    const mapStore = useMapStore()
    try {
      const resp = await backendApi.put(
        `/app/Vote/${producerName}`,
        { cubename: name.value },
      )
      if (!resp.data.bSuccess) {
        throw new Error('Error making like cube request: ' + producerName)
      }
      const existing = mapStore.getCubeByName(producerName)
      if (existing) {
        mapStore.patchGlobalCube(producerName, { numLikes: (existing.numLikes ?? 0) + 1 })
      }
      likeableCubes.value = resp.data.data.votableCubes
      likedCubes.value = resp.data.data.votedCubes
    } catch (err) {
      logError(`Error liking: ${err}.`)
    }
  }

  async function unLikeCube(producerName) {
    const mapStore = useMapStore()
    try {
      const resp = await backendApi.put(
        `/app/Vote/${producerName}/revert`,
        { cubename: name.value },
      )
      if (!resp.data.bSuccess) {
        throw new Error('Error making unlike cube request: ' + producerName)
      }
      const existing = mapStore.getCubeByName(producerName)
      if (existing) {
        mapStore.patchGlobalCube(producerName, { numLikes: Math.max(0, (existing.numLikes ?? 1) - 1) })
      }
      likeableCubes.value = resp.data.data.votableCubes
      likedCubes.value = resp.data.data.votedCubes
    } catch (err) {
      logError(`Error unLiking: ${err}.`)
    }
  }

  async function sendNickName(value: string) {
    const settingsStore = useSettingsStore()
    const accountStore = useAccountStore()

    try {
      const result = await proxyApi.put(
        `/nickname?cubeName=${encodeURIComponent(String(name.value))}&oldNickName=${encodeURIComponent(String(nickName.value))}&nickName=${encodeURIComponent(value)}`,
        {},
      )

      if (!result || result.status !== 200) {
        throw new Error(result.data.message)
      }

      nickName.value = value
      settingsStore.setContractData({ nickName: value })
      accountStore.setNickname(cube.value.cubeName, value)

    } catch (err: any) {
      logError(`Error changing nickname for cube ${name.value}: ${err}.`)

      throw err;
    }
  }

  async function changeWatchFace(settings) {
    const formsStore = useFormsStore()
    try {
      const watchFaceDTO = {
        brightnessLevel: settings.watchFaceBrightness,
        brightnessManagement: settings.watchFaceAuto,
        schedule: {
          startHour: settings.watchFaceTimerActive ? settings.watchFaceTimerSlot.start : 0,
          endHour: settings.watchFaceTimerActive ? settings.watchFaceTimerSlot.end : 24,
          outOfRangeBrightnessLevel: settings.watchFaceTimerBrightness,
        },
      }
      const result = await proxyApi.put(
        `/watchface?cubeName=${encodeURIComponent(String(name.value))}`,
        { selectedWatchFace: settings.watchFace, ...watchFaceDTO },
      )
      if (!result || result.data !== 'OK') {
        throw new Error(result.data)
      }
      const balenaBase = deviceManagerSocketHost.value
      const appResult = await balenaApi.post(
        `https://${balenaBase}/api/watch/WatchFace`,
        { watchFaceType: settings.watchFace },
      )
      if (appResult.status !== 200 || appResult.statusText !== 'OK' || !appResult.data?.bSuccess) {
        throw new Error(appResult.data.message)
      }
      watchFace.value = settings.watchFace
      watchFaceBrightness.value = settings.watchFaceBrightness
      watchFaceAuto.value = settings.watchFaceAuto
      watchFaceTimerActive.value = settings.watchFaceTimerActive
      watchFaceTimerBrightness.value = settings.watchFaceTimerBrightness
      Object.assign(watchFaceTimerSlot, settings.watchFaceTimerSlot)
      const watchResult = await balenaApi.post(
        `https://${balenaBase}/api/watch/WatchBrightnessSchedule`,
        watchFaceDTO,
      )
      if (watchResult.status !== 200 || watchResult.statusText !== 'OK') {
        throw new Error(watchResult.data.message)
      }
      formsStore.setFormState('watchFaceSettings', 'finished')
    } catch (err) {
      logError(`Error changing watch face for cube ${name.value}: ${err}.`)
      formsStore.setFormState('watchFaceSettings', 'error')
    }
  }

  async function restartCube() {
    try {
      const result = await backendApi.get('/app/restartCube', {
        params: { cubeName: name.value },
      })
      if (!result || !result.data.bSuccess) {
        throw new Error(result.data.message)
      }
    } catch (err) {
      logError(`Error restarting cube ${name.value}: ${err}.`)
    }
  }

  async function setConnectionPriority(data: { cubeName: string; priority: string; forConsumers: boolean }) {
    try {
      const priorityMap: Record<string, number> = { low: 5, verylow: 6, high: 3, veryhigh: 2 }
      const numericPriority = priorityMap[data.priority] ?? 4
      const direction = data.forConsumers ? 'p' : 'c'
      const algorithm = data.forConsumers ? 'producer_priority_trading' : 'consumer_priority_trading'
      await backendApi.put(
        `/app/CubeTradingAlgorithmDetails/${name.value}`,
        { direction, algorithm, aCubes: [{ cubeName: data.cubeName, priority: numericPriority }] },
      )
      const list = data.forConsumers ? consumers : producers
      list.value = list.value.map((c) =>
        c.cubeName === data.cubeName
          ? { ...c, tradingDetails: { ...c.tradingDetails, priority: numericPriority } }
          : c
      )
    } catch (err) {
      logError(`Error setting connection priority: ${err}.`)
    }
  }

  async function lockConnection({ producer, lock }: { producer: any; lock: boolean }) {
    try {
      const resp = await backendApi.put(
        `/app/ConnectionLock/${name.value}`,
        { consumerCubename: name.value, producerCubename: producer.cubeName, isLocked: lock },
      )
      if (resp.data.bSuccess) {
        producers.value = producers.value.map((p) =>
          p.cubeName === producer.cubeName
            ? { ...p, connectionMetadata: { ...p.connectionMetadata, isLocked: lock, lockType: 'user' } }
            : p
        )
      }
    } catch (err) {
      logError(`Error setting lock: ${err}.`)
    }
  }

  async function markNotificationsAsRead(data: { notification?: any, fromCubeName?: string, all?: boolean, isRead?: boolean }) {
    data.isRead = typeof data.isRead !== 'undefined' ? data.isRead : true

    // Optimistic update — map to new objects (never mutate store arrays directly)
    if (data.notification) {
      notifications.value = notifications.value.map(n =>
        n.id === data.notification.id ? { ...n, unread: data.isRead } : n
      )
    } else if (data.fromCubeName) {
      notifications.value = notifications.value.map(n =>
        n.triggeringcubename === data.fromCubeName ? { ...n, unread: data.isRead } : n
      )
    } else if (data.all) {
      notifications.value = notifications.value.map(n => ({ ...n, unread: data.isRead }))
    }
    let path = ''
    if (data.notification) {
      path = `/app/notifications/Notification/${data.notification.id}`
    } else if (data.fromCubeName) {
      path = `/app/notifications/TriggeringCube/${data.fromCubeName}/${name.value}`
    } else if (data.all) {
      path = `/app/notifications/Cube/${name.value}`
    }

    try {
      const result = await backendApi.put(path, { read: data.isRead })
      if (!result || !result.status.toString().startsWith('2') || !result.data.bSuccess) {
        throw new Error(result.data.Message)
      }
    } catch (err) {
      logError(`Error marking notifications as read: ${err}`)
    }
  }

  const deleteCubeImage = async () => {
    const cubeId = name.value as string

    console.debug(`Deleting profile pic for cube ${cubeId} ...`)

    const postData = new FormData()
    postData.append('cubeid', cubeId)

    try {
      const result = await reemApi.post(`/cube/deletebild/`, postData)

      if (result.status != 200) {
        throw new Error(`Could not upload image for cubeId: ${cubeId}.`)
      } else {
        console.debug(`Successfully deleted profile pic for cube ${cubeId} in REEM`)
      }
    } catch (error) {
      console.error(`Could not delete profile pic for cube ${cubeId} in REEM`, error)
    }

    // then update BPE-proxy
    try {
      const request = {
        infoWindowImageDetails: null
      };
      const response = await proxyApi.put(`/cube/?cubeName=${name.value}`, request, {});

      if (response.status != 200) {
        throw new Error("Could not update image in backend");
      }

      const cubeOnMap = mapStore.getCubeByName(cubeId)

      cubeOnMap.params.infoWindowImageDetails = {};
    } catch (error: any) {
      console.error(`Could not delete profile pic for cube ${cubeId} in backend`, error)
    }
  }

  async function updateCubeData(data: { description?: string; image?: File; imagePreviewUrl?: string, publicContactPhoneNumber?: string }) {
    const cubeId = name.value as string;

    try {
      // first update CRM directly
      const postData = new FormData()
      postData.append('cubeid', cubeId)

      if (data.image) {
        postData.append('cubebild', data.image as any)
      }

      if (data.description) {
        postData.append('cubebeschreibung', data.description)
      }

      const result = await reemApi.post(
        `/cube/uploadbild/${cubeId}`,
        postData,
      )

      if (!result || !result.data) {
        throw new Error(`cubeId: ${cubeId}.`)
      }

      let imageUrl = null;

      if (result.data?.list?.[0]) {
        const newFilename = result.data.list[0].file
        imageUrl = (images.value as any).original.url.replace(/(.*\/)[^\/]+$/, `$1${newFilename}`)

        images.value = {
          ...(images.value as any),
          original: { ...(images.value as any).original, url: imageUrl },
          thumbnail: { ...(images.value as any).thumbnail, url: imageUrl },
        }
      }

      const imageDetails = imageUrl != null
        ? images.value
        : undefined;

      // then update BPE-proxy
      const request = {
        infoWindowImageDetails: imageDetails,
        infoWindowText: data.description,
        publicContactPhoneNumber: data.publicContactPhoneNumber || ""
      };
      const response = await proxyApi.put(`/cube/?cubeName=${cubeId}`, request, {});

      if (response.status != 200) {
        throw new Error("Could not update image in backend");
      }

      if (data.description) {
        descriptionText.value = data.description
      }

      const cubeOnMap = mapStore.getCubeByName(cubeId)

      if (imageDetails != undefined) {
        cubeOnMap.params.infoWindowImageDetails = imageDetails;
      }

      if (data.description != undefined) {
        cubeOnMap.params.infoWindowText = data.description;
      }

      if (data.publicContactPhoneNumber != undefined) {
        cubeOnMap.params.publicContactPhoneNumber = data.publicContactPhoneNumber;
      }
    } catch (err) {
      logError(`Error updating cube data: ${err}`)
    } finally {
      console.debug("< updateCubeData");
    }
  }

  // --- Lifecycle ---

  /** Called by useAuthStore.clearAuth() on logout. */
  function reset() {
    id.value = false
    name.value = null
    nickName.value = false
    hasProduction.value = false
    hasConsumption.value = false
    productionType.value = ''
    latitude.value = false
    longitude.value = false
    unusedEnergyLines.value = 0
    openConnectionsAvailable.value = 0
    producers.value = []
    consumers.value = []
    connectionsReceived.value = false
    notifications.value = []
    notificationsValue.value = 0
    webPushValue.value = 0
    emailSubscription.value = false
    premiumFeatures.value = []
    likeableCubes.value = []
    likedCubes.value = []
    localHostName.value = false
    meterSocketHost.value = null
    meterSocketPort.value = null
    if (meterSocket.value) {
      meterSocket.value.close()
    }
    meterSocket.value = null
    if (deviceManagerSocket.value) {
      deviceManagerSocket.value.close()
    }
    deviceManagerSocket.value = null
  }


  function connectionPriorityFromCube(cube) {
    switch (cube.tradingDetails.priority) {
      case 1:
      case 2: return 'veryhigh'
      case 3: return 'high'
      case 4: return 'default'
      case 5: return 'low'
      case 6: return 'verylow'
      default: return "default"
    }
  }

  return {
    // State
    cube,
    id, name, nickName, hasProduction, hasConsumption, productionType,
    latitude, longitude, unusedEnergyLines, openConnectionsAvailable, totalEnergyLinesAsConsumer,
    isGrantedGridFeeDiscount,
    planName, planFrom, planTo,
    iotDeviceId, meterType, useThirdPartyMeter, showInverterData, showBatteryData, hasSeparateMeterHardware,
    producers, consumers, connectionsReceived,
    notifications, notificationsValue, webPushValue, emailSubscription,
    premiumFeatures, likeableCubes, likedCubes,
    supplier,
    localHostName,
    meterSocketHost, meterSocketPort,
    deviceManagerSocketHost, deviceManagerSocketPort, deviceManagerSocketPath,
    socketConfig, meterSocket, deviceManagerSocket,
    watchFace, watchFaceBrightness, watchFaceAuto, watchFaceTimerActive, watchFaceTimerBrightness, watchFaceTimerSlot,
    descriptionText, images, cubeCLP,
    publicContactPhoneNumber,
    // Computed
    isCubeSelected, meterSocketUrl, deviceManagerSocketUrl, homeCoordinates,
    unreadNotifications, pushNotificationsEnabled, numNotificationsForTypeGroup,
    // Actions (data population)
    setFromAppConfig, setSupplierData, setCubeConnections, addNotification,
    // Actions
    updateCubeConnections, setupMeterSocket,
    connectToCube, disconnectFromCube, donateToCube, stopDonatingToCube,
    likeCube, unLikeCube, sendNickName, changeWatchFace, restartCube, updateCubeData,
    setConnectionPriority, lockConnection,
    markNotificationsAsRead,
    // Lifecycle
    reset,
    connectionPriorityFromCube,
    deleteCubeImage
  }
})
